// src/pages/Stylesheet.js
import React, { useState } from 'react';
import {
  Sun, Moon, Users, MagnifyingGlass, ChatCircleDots,
  FileText, Table, Presentation, ClipboardText, Palette,
  CaretDown, CaretLeft, CaretRight, SignOut, User,
  Key, Eye, Plus, PencilSimple, CheckSquare, Checks,
  Trash, Link, Envelope, CalendarBlank, House,
  ListChecks, LightbulbFilament, Flag, ArrowRight,
} from '@phosphor-icons/react';
import ConventionAdKit from '../components/ConventionAdKit';
import { AD_WIDTH, AD_HEIGHT, setFont, wrapLines, fitLines, tracePill } from '../utils/conventionAd';
import '../styles/App.css';

/**
 * Handy Co Design System / Stylesheet
 * 
 * A visual showcase of the brand's colors, typography, and logo system.
 */

// ============================================
// COLOR PALETTE
// ============================================
const colors = {
  primary: [
    { name: 'Dark Forest', hex: '#19231F', description: 'Primary background' },
    { name: 'Lichen', hex: '#9CACA6', description: 'Primary accent & text' },
    { name: 'Underbrush', hex: '#5A6E65', description: 'Muted text & borders' },
  ],
  surfaces: [
    { name: 'Canopy', hex: '#3B4E45', description: 'Card surfaces & tooltips' },
    { name: 'Thicket', hex: '#2C3A33', description: 'Elevated surfaces' },
    { name: 'The Grove', hex: '#233129', description: 'Deep surfaces' },
    { name: 'Deep Roots', hex: '#1D2721', description: 'Sidebar backgrounds' },
  ],
  accents: [
    { name: 'Wild Berry', hex: '#E88888', description: 'Error & destructive' },
    { name: 'Morning Sky', hex: '#88A8E8', description: 'Info & links' },
    { name: 'Honeycomb', hex: '#E8C888', description: 'Warning & pending' },
    { name: 'Early Green', hex: '#88E8A8', description: 'Success & positive' },
    { name: 'Sleep Lilac', hex: '#C888E8', description: 'Special & highlight' },
  ],
};

// ============================================
// TYPOGRAPHY
// ============================================
const typography = [
  {
    name: 'Book Antiqua',
    family: "'Book Antiqua', Palatino, 'Palatino Linotype', serif",
    usage: 'Headlines & elegant text',
    style: 'italic',
    weights: ['400 Regular'],
    sample: 'A modern development house.',
  },
  {
    name: 'Lexend',
    family: "'Lexend', sans-serif",
    usage: 'Display titles & wordmarks',
    style: 'normal',
    weights: ['800 Extra Bold'],
    sample: 'HANDY CO',
  },
  {
    name: 'Lato',
    family: "'Lato', sans-serif",
    usage: 'Body text & UI elements',
    style: 'normal',
    weights: ['400 Regular', '500 Medium', '600 Semi Bold', '700 Bold'],
    sample: 'The quick brown fox jumps over the lazy dog.',
  },
];

// ============================================
// ICON SYSTEM — Phosphor Icons (fill weight)
// ============================================
const iconCatalog = [
  { name: 'Sun',            icon: Sun,              usage: 'Light theme toggle' },
  { name: 'Moon',           icon: Moon,             usage: 'Dark theme toggle' },
  { name: 'House',          icon: House,            usage: 'Home / Overview' },
  { name: 'Users',          icon: Users,            usage: 'Clients list' },
  { name: 'ListChecks',     icon: ListChecks,       usage: 'Tasks' },
  { name: 'ChatCircleDots', icon: ChatCircleDots,   usage: 'Thoughts / chat' },
  { name: 'LightbulbFilament', icon: LightbulbFilament, usage: 'Ideas / empty thoughts' },
  { name: 'MagnifyingGlass', icon: MagnifyingGlass, usage: 'Search' },
  { name: 'User',           icon: User,             usage: 'Profile / user menu' },
  { name: 'SignOut',         icon: SignOut,          usage: 'Sign out' },
  { name: 'Flag',           icon: Flag,             usage: 'Milestones / what\'s next' },
  { name: 'CalendarBlank',  icon: CalendarBlank,    usage: 'Dates / calendar' },
  { name: 'FileText',       icon: FileText,         usage: 'Documents' },
  { name: 'Table',          icon: Table,            usage: 'Spreadsheets' },
  { name: 'Presentation',   icon: Presentation,     usage: 'Slides / decks' },
  { name: 'ClipboardText',  icon: ClipboardText,    usage: 'Forms' },
  { name: 'Palette',        icon: Palette,          usage: 'Design / drawings' },
  { name: 'Envelope',       icon: Envelope,         usage: 'Email' },
  { name: 'Key',            icon: Key,              usage: 'Auth / login' },
  { name: 'Eye',            icon: Eye,              usage: 'Viewed' },
  { name: 'Plus',           icon: Plus,             usage: 'Create / add' },
  { name: 'PencilSimple',   icon: PencilSimple,     usage: 'Edit' },
  { name: 'CheckSquare',    icon: CheckSquare,      usage: 'Task completed' },
  { name: 'Checks',         icon: Checks,           usage: 'All done' },
  { name: 'Trash',          icon: Trash,            usage: 'Delete' },
  { name: 'Link',           icon: Link,             usage: 'Linked doc' },
  { name: 'CaretLeft',      icon: CaretLeft,        usage: 'Navigate back' },
  { name: 'CaretRight',     icon: CaretRight,       usage: 'Navigate forward' },
  { name: 'CaretDown',      icon: CaretDown,        usage: 'Expand / dropdown' },
  { name: 'ArrowRight',     icon: ArrowRight,       usage: 'View all / action' },
];

// ============================================
// PRINT COLLATERAL — Convention Ad
// ============================================
// A full-page brochure / newsletter ad at the 2400 × 3000 px (4:5, 7.2 MP)
// spec most conventions and trade associations ask sponsors for. The layout
// below paints the Handy Co composition; the preview, exports, and alt-text
// Word file come from the shared ConventionAdKit.

// The hand mark, as raw geometry, so the ad can paint it without loading the
// SVG through an <img> (which would strip the fill colour). Same artwork as
// /assets/icons/icon_handyco.svg.
const HANDYCO_MARK_VIEWBOX = 3000;
const HANDYCO_MARK_TRANSFORM = [4.4244, 0, 0, 4.4244, -763.512, -749.867];
const HANDYCO_MARK_PATH = 'M469.35,296.88C471.517,281.367 479.09,270.373 492.07,263.9C501.98,258.97 513.43,258.2 523.51,262.48C538.363,268.787 546.78,279.927 548.76,295.9C548.814,296.312 549.168,296.623 549.583,296.623C549.692,296.623 549.799,296.602 549.9,296.56C556.947,293.6 564,292.783 571.06,294.11C585.51,296.83 599.02,308.88 602.86,323.19C603.66,326.17 604.057,330.753 604.05,336.94C604.05,374.927 604.04,412.843 604.02,450.69C604.02,450.693 604.02,450.696 604.02,450.699C604.02,450.913 604.196,451.089 604.41,451.089C604.515,451.089 604.617,451.046 604.69,450.97C618.84,437.02 639.41,426.89 659.24,427.98C664.66,428.28 664.37,432.59 664.38,437.26C664.5,492.507 664.463,547.753 664.27,603C664.237,611.653 663.75,619.143 662.81,625.47C658.67,653.417 647.713,678.313 629.94,700.16C610.46,724.12 585.973,740.797 556.48,750.19C505.82,766.34 452.23,754.63 412.11,720.14C383.98,695.96 364.99,661.82 359.82,624.7C359.14,619.813 358.8,613.167 358.8,604.76C358.807,538.667 358.797,472.577 358.77,406.49C358.76,390.59 358.14,382.04 364.01,370.82C373.46,352.77 392.95,346.17 412.5,352.32C412.827,352.42 412.997,352.297 413.01,351.95C413.123,346.123 413.227,340.303 413.32,334.49C413.553,319.93 419.647,308.657 431.6,300.67C443.393,292.777 455.83,291.597 468.91,297.13C469.17,297.237 469.317,297.153 469.35,296.88ZM517.16,491.72C520.22,485.287 521.75,479.547 521.75,474.5C521.783,418.06 521.773,361.623 521.72,305.19C521.72,299.22 520.74,294.51 515.91,291.36C511.16,288.25 504.9,288.7 500.67,292.54C498.043,294.92 496.73,297.907 496.73,301.5C496.737,372.253 496.783,442.977 496.87,513.67C496.87,513.671 496.87,513.672 496.87,513.673C496.87,513.882 497.042,514.053 497.25,514.053C497.325,514.053 497.398,514.031 497.46,513.99C506.173,508.437 512.74,501.013 517.16,491.72ZM467.39,336.13C467.34,318.61 442.6,317.07 442.06,335.11C442.007,336.903 441.98,338.7 441.98,340.5C441.827,407.407 441.93,474.247 442.29,541.02C442.29,541.021 442.29,541.022 442.29,541.023C442.29,541.242 442.471,541.423 442.69,541.423C442.753,541.423 442.814,541.408 442.87,541.38L466.85,529.68C467.163,529.527 467.32,529.273 467.32,528.92C467.6,474.647 467.623,410.383 467.39,336.13ZM550.47,480.8C547.46,509.19 529.31,532.05 504.01,544.48C465.437,563.44 426.84,582.383 388.22,601.31C387.913,601.463 387.753,601.713 387.74,602.06C385.87,644.65 409.72,684.7 444.33,708.19C455.69,715.903 468.897,721.473 483.95,724.9C484.197,724.953 484.28,724.86 484.2,724.62C462.16,657.07 506.62,584.84 575.61,571.32C575.97,571.253 576.15,571.037 576.15,570.67C575.977,492.277 575.867,413.887 575.82,335.5C575.8,318.5 551.04,317.5 551,336.5C550.92,379.393 550.91,422.29 550.97,465.19C550.983,472.297 550.817,477.5 550.47,480.8ZM412.72,555.84C412.824,555.79 412.89,555.685 412.89,555.57C412.983,501.41 413.007,447.22 412.96,393C412.953,386.9 410.483,382.7 405.55,380.4C398.13,376.95 387.3,381.33 387.3,390.5C387.273,449.68 387.303,508.76 387.39,567.74C387.394,568.05 387.65,568.303 387.96,568.303C388.05,568.303 388.14,568.281 388.22,568.24L412.72,555.84ZM605.42,598.11C582.667,596.85 562.21,603.087 544.05,616.82C524.537,631.58 512.927,651.053 509.22,675.24C506.7,691.687 508.74,707.213 515.34,721.82C516.053,723.393 517.173,725.14 518.7,727.06C518.888,727.298 519.179,727.433 519.48,727.42C574.07,725.03 618.07,684.3 632.03,632.72C635.1,621.37 635.22,610.51 635.23,598.25C635.263,554.337 635.273,510.413 635.26,466.48C635.256,466.225 635.045,466.018 634.79,466.018C634.702,466.018 634.615,466.043 634.54,466.09C611.48,481.03 606.19,505.64 606.14,531.87C606.107,553.777 606.05,575.687 605.97,597.6C605.97,597.96 605.787,598.13 605.42,598.11Z';

let handycoMarkPath2D = null;
const getMarkPath = () => {
  if (!handycoMarkPath2D && typeof Path2D !== 'undefined') {
    handycoMarkPath2D = new Path2D(HANDYCO_MARK_PATH);
  }
  return handycoMarkPath2D;
};

const AD_MARGIN = 200;

const AD_FONT = {
  serif: "'Book Antiqua', Palatino, 'Palatino Linotype', serif",
  display: "'Lexend', sans-serif",
  sans: "'Lato', sans-serif",
};

const AD_FONTS_TO_LOAD = [
  "800 84px 'Lexend'",
  "400 58px 'Lato'",
  "600 36px 'Lato'",
  "700 80px 'Lato'",
];

// Three surfaces, all from the palette above. Dark Forest is the one to
// submit by default — brochure pages are usually white, so it's the ad that
// reads as a deliberate block instead of disappearing into the page.
const AD_SURFACES = [
  {
    id: 'forest',
    name: 'Dark Forest',
    swatch: '#19231F',
    swatchInk: '#9CACA6',
    bg: '#19231F',
    ghost: '#243029',
    ink: '#9CACA6',
    line: 'rgba(156, 172, 166, 0.28)',
    bandBg: '#9CACA6',
    bandInk: '#19231F',
    bandMuted: '#3B4E45',
  },
  {
    id: 'lichen',
    name: 'Lichen',
    swatch: '#9CACA6',
    swatchInk: '#19231F',
    bg: '#9CACA6',
    ghost: '#92A39C',
    ink: '#19231F',
    line: 'rgba(25, 35, 31, 0.28)',
    bandBg: '#19231F',
    bandInk: '#9CACA6',
    bandMuted: '#5A6E65',
  },
  {
    id: 'light',
    name: 'Light',
    swatch: '#F5F5F5',
    swatchInk: '#19231F',
    bg: '#F5F5F5',
    ghost: '#E9ECEA',
    ink: '#19231F',
    line: 'rgba(25, 35, 31, 0.22)',
    bandBg: '#19231F',
    bandInk: '#9CACA6',
    bandMuted: '#5A6E65',
  },
];

const AD_DEFAULTS = {
  eyebrow: 'Software · Design · Strategy',
  headline: 'Good software\nis still made\nby hand.',
  body: 'Handy Co designs and builds web apps, mobile apps, and the internal tools behind them for teams that need it done right the first time. One small team from first sketch to launch. No handoffs, no surprises.',
  points: ['Web & Mobile Apps', 'Design Systems', 'Portals & CRMs'],
  url: 'handyco.tech',
  link: 'https://handyco.tech',
  tagline: 'A modern development house.',
};

const drawMark = (ctx, { x, y, size, color }) => {
  const path = getMarkPath();
  if (!path) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / HANDYCO_MARK_VIEWBOX, size / HANDYCO_MARK_VIEWBOX);
  ctx.transform(...HANDYCO_MARK_TRANSFORM);
  ctx.fillStyle = color;
  ctx.fill(path);
  ctx.restore();
};

// Paints the whole ad in 2400 × 3000 spec units onto `ctx`, which the caller
// has already scaled. Returns the visible text in reading order so the alt
// text file is generated from the exact copy that was drawn.
const drawConventionAd = (ctx, content, surface) => {
  const W = AD_WIDTH;
  const H = AD_HEIGHT;
  const M = AD_MARGIN;
  const colW = W - M * 2;
  const bandH = 360;
  const bandY = H - bandH;
  const visible = [];

  // Page
  ctx.fillStyle = surface.bg;
  ctx.fillRect(0, 0, W, H);

  // Ghosted mark — bleeds off the right edge and tucks under the band
  drawMark(ctx, { x: 1360, y: 1150, size: 1800, color: surface.ghost });

  // Masthead: horizontal lockup, left
  const markSize = 128;
  const mastY = M;
  drawMark(ctx, { x: M, y: mastY, size: markSize, color: surface.ink });
  setFont(ctx, { family: AD_FONT.display, size: 84, weight: 800, ls: 84 * 0.06 });
  ctx.fillStyle = surface.ink;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText('HANDY CO', M + markSize + 40, mastY + markSize / 2 + 4);
  visible.push('Handy Co');

  // Masthead: eyebrow pill, right
  if (content.eyebrow) {
    const eyebrow = content.eyebrow.toUpperCase();
    setFont(ctx, { family: AD_FONT.sans, size: 36, weight: 600, ls: 36 * 0.16 });
    const textW = ctx.measureText(eyebrow).width;
    const padX = 48;
    const pillH = 96;
    const pillW = textW + padX * 2;
    const pillX = W - M - pillW;
    const pillY = mastY + (markSize - pillH) / 2;
    tracePill(ctx, pillX, pillY, pillW, pillH);
    ctx.lineWidth = 3;
    ctx.strokeStyle = surface.line;
    ctx.stroke();
    ctx.fillStyle = surface.ink;
    ctx.textBaseline = 'middle';
    // Canvas tracks after the last glyph too, so nudge by half a tracking unit
    ctx.fillText(eyebrow, pillX + padX + (36 * 0.16) / 2, pillY + pillH / 2 + 2);
    visible.push(content.eyebrow);
  }

  // Headline
  const headline = fitLines(ctx, content.headline, {
    font: (size) => ({ family: AD_FONT.serif, size, style: 'italic' }),
    max: 300, min: 160, step: 10, maxWidth: colW, maxLines: 4,
  });
  const headLH = headline.size * 1.06;
  let y = 600;
  ctx.fillStyle = surface.ink;
  ctx.textBaseline = 'alphabetic';
  headline.lines.forEach((line, i) => {
    ctx.fillText(line, M - headline.size * 0.02, y + headline.size * 0.72 + i * headLH);
  });
  y += headline.lines.length * headLH;
  visible.push(headline.lines.join(' '));

  // Body
  setFont(ctx, { family: AD_FONT.sans, size: 58, weight: 400 });
  const bodyLines = wrapLines(ctx, content.body, 1440);
  const bodyLH = 58 * 1.5;
  y += 128;
  ctx.fillStyle = surface.ink;
  ctx.globalAlpha = surface.id === 'forest' ? 0.86 : 0.8;
  bodyLines.forEach((line, i) => ctx.fillText(line, M, y + 58 * 0.72 + i * bodyLH));
  ctx.globalAlpha = 1;
  y += bodyLines.length * bodyLH;
  if (bodyLines.length) visible.push(bodyLines.join(' '));

  // Service pills
  const points = (content.points || []).map((p) => String(p || '').trim()).filter(Boolean);
  if (points.length) {
    y += 160;
    const pillH = 104;
    const padX = 44;
    const gap = 24;
    let x = M;
    setFont(ctx, { family: AD_FONT.sans, size: 36, weight: 600, ls: 36 * 0.12 });
    points.forEach((point) => {
      const label = point.toUpperCase();
      const textW = ctx.measureText(label).width;
      const pillW = textW + padX * 2;
      if (x + pillW > W - M && x > M) {
        x = M;
        y += pillH + gap;
      }
      tracePill(ctx, x, y, pillW, pillH);
      ctx.fillStyle = surface.ink;
      ctx.fill();
      ctx.fillStyle = surface.bg;
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x + padX + (36 * 0.12) / 2, y + pillH / 2 + 2);
      x += pillW + gap;
    });
    ctx.textBaseline = 'alphabetic';
    visible.push(points.join(' · '));
  }

  // Band: URL left, tagline right
  ctx.fillStyle = surface.bandBg;
  ctx.fillRect(0, bandY, W, bandH);

  const bandMid = bandY + bandH / 2;
  if (content.url) {
    setFont(ctx, { family: AD_FONT.sans, size: 80, weight: 700, ls: -0.5 });
    ctx.fillStyle = surface.bandInk;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText(content.url, M, bandMid + 4);
    visible.push(content.url);
  }
  if (content.tagline) {
    setFont(ctx, { family: AD_FONT.serif, size: 60, style: 'italic' });
    ctx.fillStyle = surface.bandInk;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';
    const tagW = ctx.measureText(content.tagline).width;
    ctx.fillText(content.tagline, W - M, bandMid + 4);
    drawMark(ctx, {
      x: W - M - tagW - 40 - 72, y: bandMid - 36, size: 72, color: surface.bandMuted,
    });
    ctx.textAlign = 'left';
    visible.push(content.tagline);
  }
  ctx.textBaseline = 'alphabetic';

  return visible;
};

/**
 * Color Swatch Component
 */
const ColorSwatch = ({ name, hex, description }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Determine if text should be light or dark based on background
  const isLight = ['#9CACA6', '#E88888', '#88A8E8', '#E8C888', '#88E8A8', '#C888E8'].includes(hex);

  return (
    <div className="ss-swatch" onClick={handleCopy}>
      <div 
        className="ss-swatch-color" 
        style={{ 
          backgroundColor: hex,
          color: isLight ? '#19231F' : '#9CACA6',
        }}
      >
        <span className="ss-swatch-hex">{copied ? 'Copied!' : hex}</span>
      </div>
      <div className="ss-swatch-info">
        <p className="ss-swatch-name">{name}</p>
        <p className="ss-swatch-desc">{description}</p>
      </div>
    </div>
  );
};

/**
 * Typography Sample Component
 */
const TypeSample = ({ name, family, usage, style, weights, sample }) => {
  return (
    <div className="ss-type-card">
      <div className="ss-type-header">
        <h3 className="ss-type-name" style={{ fontFamily: family, fontStyle: style }}>
          {name}
        </h3>
        <span className="ss-type-usage">{usage}</span>
      </div>
      <p 
        className="ss-type-sample" 
        style={{ fontFamily: family, fontStyle: style }}
      >
        {sample}
      </p>
      <div className="ss-type-meta">
        <div className="ss-type-weights">
          {weights.map((weight, i) => (
            <span key={i} className="ss-type-weight">{weight}</span>
          ))}
        </div>
        <code className="ss-type-family">{family}</code>
      </div>
    </div>
  );
};

/**
 * Hand Icon Component (from site)
 */
const HandIcon = ({ size = 120, color = '#9CACA6' }) => (
  <div
    role="img"
    aria-label="Handy Co Logo"
    style={{
      width: size,
      height: size,
      backgroundColor: color,
      WebkitMaskImage: 'url(/assets/icons/icon_handyco.svg)',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskImage: 'url(/assets/icons/icon_handyco.svg)',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      flexShrink: 0,
    }}
  />
);

/**
 * Main Stylesheet Page
 */
const Stylesheet = () => {
  return (
    <div className="ss-page">
      {/* Header */}
      <header className="ss-header">
        <a href="/" className="ss-back">← Back to site</a>
        <span className="ss-badge">Design System</span>
      </header>

      {/* Hero - Matches Homepage Exactly */}
      <section className="ss-hero">
        <HandIcon size={120} />
        <h1 className="ss-hero-title">HANDY CO</h1>
        <p className="ss-hero-subtitle">Brand & Design System</p>
      </section>

      {/* Logo Section */}
      <section className="ss-section">
        <div className="ss-section-header">
          <h2 className="ss-section-title">Logo</h2>
          <p className="ss-section-desc">
            The Handy Co mark represents craftsmanship and human touch in a digital world.
          </p>
        </div>

        <div className="ss-logo-grid">
          {/* Icon Only */}
          <div className="ss-logo-card">
            <div className="ss-logo-preview dark">
              <HandIcon size={100} color="#9CACA6" />
            </div>
            <p className="ss-logo-label">Icon Mark</p>
            <p className="ss-logo-desc">Primary symbol, use when space is limited</p>
          </div>

          {/* Wordmark */}
          <div className="ss-logo-card">
            <div className="ss-logo-preview dark">
              <span className="ss-wordmark">HANDY CO</span>
            </div>
            <p className="ss-logo-label">Wordmark</p>
            <p className="ss-logo-desc">Lexend Extra Bold, tracked wide</p>
          </div>

          {/* Lockup Vertical */}
          <div className="ss-logo-card">
            <div className="ss-logo-preview dark">
              <div className="ss-lockup-vertical">
                <HandIcon size={80} color="#9CACA6" />
                <span className="ss-wordmark-vertical">HANDY CO</span>
              </div>
            </div>
            <p className="ss-logo-label">Vertical Lockup</p>
            <p className="ss-logo-desc">Stacked arrangement for square contexts</p>
          </div>

          {/* Lockup Horizontal */}
          <div className="ss-logo-card">
            <div className="ss-logo-preview dark">
              <div className="ss-lockup-horizontal">
                <HandIcon size={56} color="#9CACA6" />
                <span className="ss-wordmark-horizontal">HANDY CO</span>
              </div>
            </div>
            <p className="ss-logo-label">Horizontal Lockup</p>
            <p className="ss-logo-desc">Inline arrangement for headers</p>
          </div>

          {/* Light Background */}
          <div className="ss-logo-card">
            <div className="ss-logo-preview light">
              <HandIcon size={100} color="#19231F" />
            </div>
            <p className="ss-logo-label">On Light</p>
            <p className="ss-logo-desc">Dark Forest icon on light backgrounds</p>
          </div>

          {/* Accent Background */}
          <div className="ss-logo-card">
            <div className="ss-logo-preview accent">
              <HandIcon size={100} color="#19231F" />
            </div>
            <p className="ss-logo-label">On Lichen</p>
            <p className="ss-logo-desc">Dark Forest icon on brand accent</p>
          </div>
        </div>
      </section>

      {/* Colors Section */}
      <section className="ss-section">
        <div className="ss-section-header">
          <h2 className="ss-section-title">Color Palette</h2>
          <p className="ss-section-desc">
            A forest-inspired palette that feels grounded, sophisticated, and alive.
            Click any swatch to copy its hex value.
          </p>
        </div>

        {/* Primary Colors */}
        <div className="ss-color-group">
          <h3 className="ss-color-group-title">Primary</h3>
          <div className="ss-swatches">
            {colors.primary.map((color) => (
              <ColorSwatch key={color.hex} {...color} />
            ))}
          </div>
        </div>

        {/* Surface Colors */}
        <div className="ss-color-group">
          <h3 className="ss-color-group-title">Surfaces</h3>
          <div className="ss-swatches">
            {colors.surfaces.map((color) => (
              <ColorSwatch key={color.hex} {...color} />
            ))}
          </div>
        </div>

        {/* Accent Colors */}
        <div className="ss-color-group">
          <h3 className="ss-color-group-title">Accents</h3>
          <div className="ss-swatches">
            {colors.accents.map((color) => (
              <ColorSwatch key={color.hex} {...color} />
            ))}
          </div>
        </div>
      </section>

      {/* Typography Section */}
      <section className="ss-section">
        <div className="ss-section-header">
          <h2 className="ss-section-title">Typography</h2>
          <p className="ss-section-desc">
            A blend of classical elegance and modern clarity.
          </p>
        </div>

        <div className="ss-type-grid">
          {typography.map((type) => (
            <TypeSample key={type.name} {...type} />
          ))}
        </div>

        {/* Type Scale */}
        <div className="ss-type-scale">
          <h3 className="ss-color-group-title">Type Scale</h3>
          <div className="ss-scale-samples">
            <div className="ss-scale-item">
              <span className="ss-scale-text" style={{ fontSize: '72px', fontFamily: "'Lexend', sans-serif", fontWeight: 800 }}>Aa</span>
              <span className="ss-scale-label">100px / Display</span>
            </div>
            <div className="ss-scale-item">
              <span className="ss-scale-text" style={{ fontSize: '42px', fontFamily: "'Book Antiqua', serif", fontStyle: 'italic' }}>Aa</span>
              <span className="ss-scale-label">50px / Hero</span>
            </div>
            <div className="ss-scale-item">
              <span className="ss-scale-text" style={{ fontSize: '28px', fontFamily: "'Book Antiqua', serif", fontStyle: 'italic' }}>Aa</span>
              <span className="ss-scale-label">32px / Section Title</span>
            </div>
            <div className="ss-scale-item">
              <span className="ss-scale-text" style={{ fontSize: '20px', fontFamily: "'Book Antiqua', serif", fontStyle: 'italic' }}>Aa</span>
              <span className="ss-scale-label">22px / Card Heading</span>
            </div>
            <div className="ss-scale-item">
              <span className="ss-scale-text" style={{ fontSize: '18px', fontFamily: "'Lato', sans-serif" }}>Aa</span>
              <span className="ss-scale-label">20px / Body Large</span>
            </div>
            <div className="ss-scale-item">
              <span className="ss-scale-text" style={{ fontSize: '15px', fontFamily: "'Lato', sans-serif" }}>Aa</span>
              <span className="ss-scale-label">15px / Body</span>
            </div>
            <div className="ss-scale-item">
              <span className="ss-scale-text" style={{ fontSize: '12px', fontFamily: "'Lato', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Aa</span>
              <span className="ss-scale-label">12px / Label</span>
            </div>
          </div>
        </div>
      </section>

      {/* Icon System Section */}
      <section className="ss-section">
        <div className="ss-section-header">
          <h2 className="ss-section-title">Icon System</h2>
          <p className="ss-section-desc">
            Phosphor Icons with <strong>fill</strong> weight. Solid, grounded shapes
            that complement the warm typographic palette. Every icon renders at the
            current text color by default.
          </p>
        </div>

        <div className="ss-icon-grid">
          {iconCatalog.map((ic) => {
            const Icon = ic.icon;
            return (
              <div key={ic.name} className="ss-icon-item">
                <Icon weight="fill" size={24} />
                <span className="ss-icon-name">{ic.name}</span>
                <span className="ss-icon-usage">{ic.usage}</span>
              </div>
            );
          })}
        </div>

        <div className="ss-icon-meta">
          <code className="ss-type-family">@phosphor-icons/react — weight="fill"</code>
        </div>
      </section>

      {/* Print Collateral Section */}
      <section className="ss-section" id="convention-ad">
        <div className="ss-section-header">
          <h2 className="ss-section-title">Print Collateral</h2>
          <p className="ss-section-desc">
            A full-page convention ad at the spec every sponsor form asks for:
            a 2400 × 3000 JPEG, a Word file with the visible text for alt text,
            and a hyperlink. Edit the copy, pick a surface, download all three.
            The preview is the file.
          </p>
        </div>

        <ConventionAdKit
          draw={drawConventionAd}
          surfaces={AD_SURFACES}
          defaults={AD_DEFAULTS}
          fonts={AD_FONTS_TO_LOAD}
          slug="handyco-convention-ad"
          docTitle="Handy Co — Convention Ad"
          altIntro="Advertisement for Handy Co, a modern development house."
          pointsLabel="Services"
          classPrefix="ss-ad"
        />

        <div className="ss-ad-notes">
          <div className="ss-ad-note">
            <h3 className="ss-color-group-title">Submitting</h3>
            <p>
              Send the JPEG as-is. It's 7.2 megapixels with 300 DPI stamped in,
              so it places at 8 × 10 in and survives a printed brochure. The
              Word file is the alt text; the hyperlink is the one under Hyperlink.
            </p>
          </div>
          <div className="ss-ad-note">
            <h3 className="ss-color-group-title">Writing it</h3>
            <p>
              One idea in the headline, set on hard line breaks so it reads as
              a sentence. Keep the body to three lines and the services to three
              pills. If the sponsor wants a booth number or session, put it in
              the eyebrow.
            </p>
          </div>
        </div>
      </section>

      {/* Spacing & Radius Section */}
      <section className="ss-section">
        <div className="ss-section-header">
          <h2 className="ss-section-title">Spacing & Radius</h2>
          <p className="ss-section-desc">
            Consistent spacing creates rhythm; rounded corners add warmth.
          </p>
        </div>

        <div className="ss-spacing-grid">
          {/* Border Radius */}
          <div className="ss-spacing-card">
            <h3 className="ss-color-group-title">Border Radius</h3>
            <div className="ss-radius-samples">
              <div className="ss-radius-item">
                <div className="ss-radius-box" style={{ borderRadius: '8px' }}></div>
                <span>8px — Inputs, buttons</span>
              </div>
              <div className="ss-radius-item">
                <div className="ss-radius-box" style={{ borderRadius: '12px' }}></div>
                <span>12px — Cards</span>
              </div>
              <div className="ss-radius-item">
                <div className="ss-radius-box" style={{ borderRadius: '16px' }}></div>
                <span>16px — Modals</span>
              </div>
              <div className="ss-radius-item">
                <div className="ss-radius-box" style={{ borderRadius: '20px' }}></div>
                <span>20px — Pills, chips</span>
              </div>
              <div className="ss-radius-item">
                <div className="ss-radius-box" style={{ borderRadius: '50px' }}></div>
                <span>50px — Full round</span>
              </div>
            </div>
          </div>

          {/* Spacing Scale */}
          <div className="ss-spacing-card">
            <h3 className="ss-color-group-title">Spacing Scale</h3>
            <div className="ss-space-samples">
              {[4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 100].map((size) => (
                <div key={size} className="ss-space-item">
                  <div className="ss-space-bar" style={{ width: size }}></div>
                  <span>{size}px</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="ss-footer">
        <HandIcon size={40} color="#5A6E65" />
        <p>Handy Co - 2025</p>
      </footer>
    </div>
  );
};

export default Stylesheet;
